/**
 * Schedule matching and per-hour zone property resolution for the space
 * demand simulation. Each {@link EnergyZoneActivity} can carry a list of
 * {@link ActivitySchedule} entries; matching schedules override base zone
 * properties (last match wins).
 */
import { HourNumbers, WeekdayNumbers, WeekNumbers } from 'luxon';
import { HOURS_PER_YEAR } from '@shared/constants/math.constants';
import { getOccupantCountPerM2 } from '../activities-residents.helpers';
import { EnergyZoneActivity } from './energy-activity.helpers';
import { DEFAULT_INDOOR_TEMP } from './energy.models';
import { ActivitySchedule } from './space-activity.helpers';

/**
 * Point in simulated time used to match activity schedules.
 * Hour is 0–23, weekday is 1–7 (Luxon convention), week is 1–53.
 */
export type SimulationTime = {
  hour: HourNumbers;
  weekday: WeekdayNumbers;
  week: WeekNumbers;
};

/**
 * Zone properties resolved for a specific hour, combining the zone's base
 * properties with any matching schedule overrides (last match wins).
 */
export type ResolvedZoneProps = {
  /** Heating setpoint (°C) */
  heatSet: number;
  /** Cooling setpoint (°C), already adjusted for deadband */
  coolSet: number;
  /** Deadband width between heat and cool setpoints (K) */
  deadbandK: number;
  /** Max delivered heating power for this hour (W/m²) */
  heatPowerCap?: number;
  /** Max delivered cooling power for this hour (W/m²) */
  coolPowerCap?: number;
  /** Ventilation flow per zone floor area (m³/s·m²) */
  ventFlowPerM2_m3s?: number;
  /** Minimum supply air temperature after heat recovery (°C) */
  postHeatingTemp?: number;
  /** Maximum supply air temperature before cooling coil (°C) */
  preCoolingTemp?: number;
  /** Occupants per m² of zone floor area */
  occupantCountPerM2?: number;
  /** Scales {@link getActivityGainInWPerM2} for this hour (0–1 typical) */
  activityMultiplier: number;
  /** Scales occupant heat gain for this hour (0–1 typical) */
  occupantMultiplier: number;
};

/**
 * Schedules may use the legacy key ventFlowPerM2_m3s for vent flow overrides.
 */
type ScheduleProperties = ActivitySchedule['properties'] & {
  ventFlowPerM2_m3s?: number;
};

type ScheduleRule = number | [number, number];

/** Zone base properties before schedule overrides are applied */
type ZoneBaseProps = {
  heatSet: number;
  deadbandK: number;
  coolSet: number;
  heatPowerCap?: number;
  coolPowerCap?: number;
  ventFlowPerM2_m3s?: number;
  postHeatingTemp?: number;
  preCoolingTemp?: number;
  occupantCountPerM2?: number;
};

/**
 * Schedule-only overrides for one hour. Fields left undefined keep the zone base
 * value; multipliers always have a value (default 1 when no schedule matches).
 */
export type ScheduleLayerState = {
  heatSet?: number;
  coolSet?: number;
  deadbandK?: number;
  heatPowerCap?: number;
  coolPowerCap?: number;
  ventFlowPerM2_m3s?: number;
  postHeatingTemp?: number;
  preCoolingTemp?: number;
  occupantCountPerM2?: number;
  activityMultiplier: number;
  occupantMultiplier: number;
};

/**
 * Match a value against a rule: an exact number or an inclusive [start, end] range.
 * @param value Value to test
 * @param rule Exact number or [start, end] range
 * @param allowWrap When true, [a, b] with a > b wraps over the boundary (e.g. hours [22, 6], weeks [50, 5])
 * @returns True when the value matches the rule
 */
const matchesRule = (
  value: number,
  rule: ScheduleRule,
  allowWrap = false,
): boolean => {
  if (typeof rule === 'number') return value === rule;
  const [start, end] = rule;
  if (!allowWrap || start <= end) return value >= start && value <= end;
  // Wrap: start > end => value ∈ [start, max] ∪ [min, end]
  return value >= start || value <= end;
};

/**
 * Match a value against any of the supplied rules, see {@link matchesRule}.
 */
const matchesAnyRule = (
  value: number,
  rules: ScheduleRule[],
  allowWrap = false,
): boolean => rules.some((rule) => matchesRule(value, rule, allowWrap));

/**
 * Check if a schedule is active at the given simulation time.
 * Empty operation fields match every hour/weekday/week.
 * @param schedule Schedule to test, see {@link ActivitySchedule}
 * @param time Simulation time to test against
 * @returns True when the schedule applies at the given time
 */
export const isScheduleActive = (
  schedule: ActivitySchedule,
  { hour, weekday, week }: SimulationTime,
): boolean => {
  const { hours, wdays, weeks } = schedule.operation ?? {};

  // Weeks (allow wrap, e.g. [50, 5])
  if (typeof weeks === 'number') {
    if (week !== weeks) return false;
  } else if (Array.isArray(weeks) && weeks.length > 0) {
    if (!matchesAnyRule(week, weeks, true)) return false;
  }

  // Weekdays (no wrap)
  if (typeof wdays === 'number') {
    if (weekday !== wdays) return false;
  } else if (Array.isArray(wdays) && wdays.length > 0) {
    if (!matchesAnyRule(weekday, wdays, false)) return false;
  }

  // Hours (allow wrap, e.g. [22, 6])
  if (Array.isArray(hours) && hours.length > 0) {
    if (!matchesAnyRule(hour, hours, true)) return false;
  }

  return true;
};

/**
 * Apply deadband to ensure proper temperature setpoint separation.
 * @param heatSet Heating setpoint temperature (°C)
 * @param coolSet Cooling setpoint temperature (°C)
 * @param deadbandK Deadband width in Kelvin
 * @returns Adjusted cooling setpoint ensuring minimum separation
 */
export const applyDeadband = (
  heatSet: number,
  coolSet: number,
  deadbandK: number,
): number => {
  const minCool = heatSet + Math.max(0, deadbandK);
  return coolSet < minCool ? minCool : coolSet;
};

/**
 * Read zone base properties before schedule overrides.
 * @param activity Energy zone activity
 * @returns Base zone properties
 */
const getZoneBaseProps = (activity: EnergyZoneActivity): ZoneBaseProps => {
  const heatSet = activity.properties.heatSet ?? DEFAULT_INDOOR_TEMP;
  const deadbandK = activity.properties.deadbandK ?? 2;
  return {
    heatSet,
    deadbandK,
    coolSet: activity.properties.coolSet ?? heatSet + deadbandK + 1,
    heatPowerCap: activity.properties.heatPowerLimitWPerM2,
    coolPowerCap: activity.properties.coolPowerLimitWPerM2,
    ventFlowPerM2_m3s: activity.properties.ventFlowPerM2InM3PerS,
    postHeatingTemp: activity.properties.postHeatingTemp,
    preCoolingTemp: activity.properties.preCoolingTemp,
    occupantCountPerM2:
      activity.properties.occupantCountPerM2 ??
      getOccupantCountPerM2(activity.type),
  };
};

/**
 * Resolve schedule-only overrides for one hour (last matching schedule wins).
 * @param schedules Activity schedules, if any
 * @param time Simulation time
 * @returns Schedule layer applied on top of zone base properties
 */
const getScheduleLayerForHour = (
  schedules: ActivitySchedule[] | undefined,
  time: SimulationTime,
): ScheduleLayerState => {
  const layer: ScheduleLayerState = {
    activityMultiplier: 1,
    occupantMultiplier: 1,
  };

  for (const schedule of schedules ?? []) {
    if (!isScheduleActive(schedule, time)) continue;
    const p: ScheduleProperties = schedule.properties;

    if (p.heatSet != null) layer.heatSet = p.heatSet;
    if (p.coolSet != null) layer.coolSet = p.coolSet;
    if (p.deadbandK != null) layer.deadbandK = p.deadbandK;

    if (p.heatPowerLimitWPerM2 != null)
      layer.heatPowerCap = p.heatPowerLimitWPerM2;
    if (p.coolPowerLimitWPerM2 != null)
      layer.coolPowerCap = p.coolPowerLimitWPerM2;

    if (p.ventFlowPerM2_m3s != null)
      layer.ventFlowPerM2_m3s = p.ventFlowPerM2_m3s;
    if (p.postHeatingTemp != null) layer.postHeatingTemp = p.postHeatingTemp;
    if (p.preCoolingTemp != null) layer.preCoolingTemp = p.preCoolingTemp;

    if (p.occupantCountPerM2 != null) {
      layer.occupantCountPerM2 = p.occupantCountPerM2;
    }

    if (p.activityMultiplier != null)
      layer.activityMultiplier = p.activityMultiplier;
    if (p.occupantMultiplier != null) {
      layer.occupantMultiplier = p.occupantMultiplier;
    }
  }

  return layer;
};

/**
 * Merge zone base properties with a schedule layer and apply deadband.
 * @param base Zone base properties
 * @param layer Schedule overrides for the hour
 * @returns Resolved properties for the hour
 */
const mergeZoneProps = (
  base: ZoneBaseProps,
  layer: ScheduleLayerState,
): ResolvedZoneProps => {
  const heatSet = layer.heatSet ?? base.heatSet;
  const deadbandK = layer.deadbandK ?? base.deadbandK;
  const coolSet = layer.coolSet ?? base.coolSet;

  return {
    heatSet,
    coolSet: applyDeadband(heatSet, coolSet, deadbandK),
    deadbandK,
    heatPowerCap: layer.heatPowerCap ?? base.heatPowerCap,
    coolPowerCap: layer.coolPowerCap ?? base.coolPowerCap,
    ventFlowPerM2_m3s: layer.ventFlowPerM2_m3s ?? base.ventFlowPerM2_m3s,
    postHeatingTemp: layer.postHeatingTemp ?? base.postHeatingTemp,
    preCoolingTemp: layer.preCoolingTemp ?? base.preCoolingTemp,
    occupantCountPerM2: layer.occupantCountPerM2 ?? base.occupantCountPerM2,
    activityMultiplier: layer.activityMultiplier,
    occupantMultiplier: layer.occupantMultiplier,
  };
};

/**
 * Resolve the effective zone properties for a given hour.
 * Starts from the zone's base properties and applies every matching
 * schedule in order (last match wins), then enforces the deadband.
 * @param activity Energy zone activity, see {@link EnergyZoneActivity}
 * @param time Simulation time to resolve schedules for
 * @returns Resolved properties, see {@link ResolvedZoneProps}
 */
export const resolveZonePropsForHour = (
  activity: EnergyZoneActivity,
  time: SimulationTime,
): ResolvedZoneProps =>
  mergeZoneProps(
    getZoneBaseProps(activity),
    getScheduleLayerForHour(activity.schedules, time),
  );

/**
 * Advance simulation time by one hour without allocating a new object.
 * Used when precomputing hourly timelines to avoid 8760 allocations per zone.
 * @param time Mutable simulation time
 */
export const advanceSimulationTimeInPlace = (time: SimulationTime): void => {
  const nextHour = ((time.hour + 1) % 24) as HourNumbers;
  time.hour = nextHour;
  if (nextHour !== 0) return;

  time.weekday = ((time.weekday % 7) + 1) as WeekdayNumbers;
  if (time.weekday === 1) {
    time.week = ((time.week % 53) + 1) as WeekNumbers;
  }
};

/** Per-simulation cache of schedule layers keyed by the schedules array reference */
export type ScheduleLayerTimelineCache = Map<
  ActivitySchedule[] | undefined,
  ScheduleLayerState[]
>;

/** Per-simulation cache of zone timelines: schedules reference → base props key → timeline */
export type ZonePropsTimelineCache = Map<
  ActivitySchedule[] | undefined,
  Map<string, ResolvedZoneProps[]>
>;

/**
 * Normalize schedule input for cache lookup.
 * Empty arrays are treated as no schedules (same dependency as undefined).
 * @param schedules Activity schedules
 * @returns Schedules reference used as cache dependency, or undefined
 */
const getScheduleDependency = (
  schedules: ActivitySchedule[] | undefined,
): ActivitySchedule[] | undefined =>
  schedules?.length ? schedules : undefined;

/**
 * Build or reuse an 8760-hour schedule layer timeline.
 * Zones that share the same {@link ActivitySchedule} array reference reuse one
 * timeline instead of re-matching schedules for every zone × hour.
 * The schedules array is the cache dependency (reference identity), not a serialized key.
 * @param schedules Activity schedules
 * @param startTime Simulation start time
 * @param cache Mutable cache populated during zone setup
 * @returns Schedule layer for each hour of the year
 */
const getScheduleLayerTimeline = (
  schedules: ActivitySchedule[] | undefined,
  startTime: SimulationTime,
  cache: ScheduleLayerTimelineCache,
): ScheduleLayerState[] => {
  const scheduleDependency = getScheduleDependency(schedules);
  const cached = cache.get(scheduleDependency);
  if (cached) return cached;

  const timeline = new Array<ScheduleLayerState>(HOURS_PER_YEAR);
  const time: SimulationTime = { ...startTime };
  for (let hourIndex = 0; hourIndex < HOURS_PER_YEAR; hourIndex++) {
    timeline[hourIndex] = getScheduleLayerForHour(schedules, time);
    advanceSimulationTimeInPlace(time);
  }

  cache.set(scheduleDependency, timeline);
  return timeline;
};

/**
 * Cache key for zone base properties (excludes schedules — those are a separate dependency).
 * @param activity Energy zone activity
 * @returns Cache key string for zone-specific base props
 */
const getZoneBasePropsCacheKey = (activity: EnergyZoneActivity): string => {
  const p = activity.properties;
  return JSON.stringify({
    type: activity.type,
    programType: activity.programType,
    heatSet: p.heatSet,
    deadbandK: p.deadbandK,
    coolSet: p.coolSet,
    heatPowerLimitWPerM2: p.heatPowerLimitWPerM2,
    coolPowerLimitWPerM2: p.coolPowerLimitWPerM2,
    ventFlowPerM2InM3PerS: p.ventFlowPerM2InM3PerS,
    postHeatingTemp: p.postHeatingTemp,
    preCoolingTemp: p.preCoolingTemp,
    occupantCountPerM2: p.occupantCountPerM2,
  });
};

/**
 * Precompute resolved zone properties for all 8760 hours.
 * Moves schedule matching out of the hot hourly simulation loop.
 * @param activity Energy zone activity
 * @param startTime Simulation start time
 * @param scheduleLayerCache Shared schedule layer cache across zones
 * @param zonePropsCache Shared resolved timeline cache for identical zones
 * @returns Resolved properties indexed by hour (0–8759)
 */
export const buildZonePropsTimeline = (
  activity: EnergyZoneActivity,
  startTime: SimulationTime,
  scheduleLayerCache: ScheduleLayerTimelineCache,
  zonePropsCache: ZonePropsTimelineCache,
): ResolvedZoneProps[] => {
  const scheduleDependency = getScheduleDependency(activity.schedules);
  const basePropsKey = getZoneBasePropsCacheKey(activity);

  let timelinesByBaseProps = zonePropsCache.get(scheduleDependency);
  if (!timelinesByBaseProps) {
    timelinesByBaseProps = new Map();
    zonePropsCache.set(scheduleDependency, timelinesByBaseProps);
  }

  const cachedTimeline = timelinesByBaseProps.get(basePropsKey);
  if (cachedTimeline) return cachedTimeline;

  const base = getZoneBaseProps(activity);

  // Zones without schedules have constant props every hour
  if (!scheduleDependency) {
    const props = mergeZoneProps(base, {
      activityMultiplier: 1,
      occupantMultiplier: 1,
    });
    const timeline = Array.from({ length: HOURS_PER_YEAR }, () => props);
    timelinesByBaseProps.set(basePropsKey, timeline);
    return timeline;
  }

  const scheduleLayers = getScheduleLayerTimeline(
    activity.schedules,
    startTime,
    scheduleLayerCache,
  );
  const timeline = scheduleLayers.map((layer) => mergeZoneProps(base, layer));
  timelinesByBaseProps.set(basePropsKey, timeline);
  return timeline;
};

/**
 * Advance the simulation time by one hour, rolling over weekday and week.
 * @param time Current simulation time
 * @returns The next simulation time
 */
export const advanceSimulationTime = (time: SimulationTime): SimulationTime => {
  const next: SimulationTime = { ...time };
  advanceSimulationTimeInPlace(next);
  return next;
};
